import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  Box,
} from "@mui/material";

import { blueTextFieldStyles } from "assets/themes/textFieldStyles";
import default_theme from "assets/themes/themes";
import TooltipLabel from "components/Common/TooltipLabel";
import PrimaryOutlinedButton from "components/Common/PrimaryOutlinedButton";
import DangerButton from "components/Common/DangerButton";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import PublicIcon from "@mui/icons-material/Public";
import ParkIcon from "@mui/icons-material/Park";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import InputAdornment from "@mui/material/InputAdornment";
import GroupsIcon from "@mui/icons-material/Groups";

const StepOneDialog = ({
  open,
  onClose,
  onSave,
  formData,
  setFormData,
  currentIndex,
  rdds,
  domains,
}) => {
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 1, color: "#23576B" }}
        >
          {currentIndex !== null
            ? `Edit Stakeholder: ${(`${formData?.first_name || ""} ${formData?.last_name || ""}`).trim() || "Unnamed stakeholder"}`
            : "Add Stakeholder"}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>

        <TextField
          label="First Name"
          value={formData?.first_name || ""}
          onChange={(e) => handleChange("first_name", e.target.value)}
          fullWidth
          sx={blueTextFieldStyles(default_theme)}
        />

        <TextField
          label="Last Name"
          value={formData?.last_name || ""}
          onChange={(e) => handleChange("last_name", e.target.value)}
          fullWidth
          sx={blueTextFieldStyles(default_theme)}
        />

        <TextField
          label="Contact Details"
          value={formData?.contact_details || ""}
          onChange={(e) => handleChange("contact_details", e.target.value)}
          fullWidth
          sx={blueTextFieldStyles(default_theme)}
        />

        <TextField
          label="Brief Description"
          value={formData?.brief_description || ""}
          onChange={(e) => handleChange("brief_description", e.target.value)}
          fullWidth
          multiline
          rows={3}
          sx={blueTextFieldStyles(default_theme)}
        />

        {/* RDD */}
        {/* RDD SELECTION (CARD STYLE) */}
        <TextField
          select
          label="RDD"
          value={formData?.rdd_id || ""}
          onChange={(e) => handleChange("rdd_id", e.target.value || null)}
          fullWidth
          sx={blueTextFieldStyles(default_theme)}
        >
          <MenuItem value="">None</MenuItem>

          {rdds.map((rdd) => (
            <MenuItem key={rdd.id} value={rdd.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>

                {/* IMAGE */}
                {rdd.image_url && (
                  <Box
                    component="img"
                    src={rdd.image_url}
                    alt={rdd.name}
                    sx={{
                      width: 24,
                      height: 24,
                      objectFit: "contain",
                    }}
                  />
                )}

                {/* NAME */}
                <Typography
                  sx={{
                    color: rdd.color,
                    fontWeight: 500,
                  }}
                >
                  {rdd.name}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>

        {/* DOMAIN */}
        <TextField
          select
          label={
            <TooltipLabel
              label="Domain"
              tooltip={
                <>
                  <strong>Policy</strong> – stakeholders that can influence local politics, policies and local development (e.g municipalities, regional offices)
                  <br /><br />
                  <strong>Research</strong> – stakeholders that can give input and support or research and development (e.g. school, university, research centers)
                  <br /><br />
                  <strong>Industry</strong> – stakeholders that can encourage the creation and adaptation of business models, development of products and offer of services, provide financial support (e.g. hospitality facilities, third sector partners, museums, cultural startups)
                  <br /><br />
                  <strong>Public</strong> – stakeholders in the community ( e.g.local cultural institution, associations and groups)
                </>
              }
            />
          }
          value={formData?.domain_id || ""}
          onChange={(e) => handleChange("domain_id", e.target.value || null)}
          fullWidth
          sx={blueTextFieldStyles(default_theme)}
        >
          <MenuItem value="">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: "#B0BEC5",
                }}
              />

              <Typography
                sx={{
                  fontStyle: "italic",
                  color: "#78909C",
                  fontWeight: 500,
                }}
              >
                No Domain Selected
              </Typography>
            </Box>
          </MenuItem>

          {domains.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: d.color || "#23576B",
                  }}
                />

                <Typography sx={{ fontWeight: 500 }}>
                  {d.name}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Age"
          type="number"
          value={formData?.age || ""}
          onChange={(e) => handleChange("age", e.target.value)}
          fullWidth
          sx={blueTextFieldStyles(default_theme)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutlineIcon
                  sx={{
                    color: "#7898A4",
                    fontSize: 20,
                  }}
                />
              </InputAdornment>
            ),
            inputProps: {
              min: 0,
              max: 120,
            },
          }}
        />

        <TextField
          select
          label="Gender"
          value={formData?.gender || ""}
          onChange={(e) => handleChange("gender", e.target.value)}
          fullWidth
          sx={blueTextFieldStyles(default_theme)}
        >
          <MenuItem value="Male">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <MaleIcon
                sx={{
                  color: "#23576B",
                  fontSize: 20,
                }}
              />

              <Typography fontWeight={500}>
                Male
              </Typography>
            </Box>
          </MenuItem>

          <MenuItem value="Female">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <FemaleIcon
                sx={{
                  color: "#C15C99",
                  fontSize: 20,
                }}
              />

              <Typography fontWeight={500}>
                Female
              </Typography>
            </Box>
          </MenuItem>
        </TextField>

        <TextField
          label={
            <TooltipLabel
              label="GREU"
              tooltip="GREU (Groups at risk of social exclusion and underrepresentation) are stakeholders at risk of exclusion due to factors like physical disabilities, age, ethnic origins, religious beliefs, and other intersecting aspects. Historically, these groups have been underrepresented and largely excluded from decision-making processes, especially in rural areas."
            />
          }
          placeholder="Enter a GREU group"
          value={formData?.grue || ""}
          onChange={(e) => handleChange("grue", e.target.value)}
          fullWidth
          sx={blueTextFieldStyles(default_theme)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <GroupsIcon
                  sx={{
                    color: "#E53935",
                    fontSize: 20,
                  }}
                />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          label={
            <TooltipLabel
              label="Cross-cutting priority"
              tooltip="CCP - Cross-cutting priorities are core foundational pillars that underpin and guide a sustainable transition in rural areas. In RURACTIVE, these priorities are climate change adaptation and mitigation, biodiversity, and social justice and inclusion."
            />
          }
          value={formData?.cross_cutting_priority || ""}
          onChange={(e) =>
            handleChange("cross_cutting_priority", e.target.value)
          }
          fullWidth
          sx={blueTextFieldStyles(default_theme)}
        >
          <MenuItem value="">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <RemoveCircleOutlineIcon
                sx={{
                  color: "#90A4AE",
                  fontSize: 20,
                }}
              />

              <Typography
                sx={{
                  fontStyle: "italic",
                  color: "#78909C",
                  fontWeight: 500,
                }}
              >
                None Selected
              </Typography>
            </Box>
          </MenuItem>

          <MenuItem value="climate">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PublicIcon
                sx={{
                  color: "#2E7D32",
                  fontSize: 20,
                }}
              />

              <Typography fontWeight={500}>
                Climate change adaptation & mitigation
              </Typography>
            </Box>
          </MenuItem>

          <MenuItem value="biodiversity">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ParkIcon
                sx={{
                  color: "#43A047",
                  fontSize: 20,
                }}
              />

              <Typography fontWeight={500}>
                Biodiversity
              </Typography>
            </Box>
          </MenuItem>

          <MenuItem value="social">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Diversity3Icon
                sx={{
                  color: "#8E24AA",
                  fontSize: 20,
                }}
              />

              <Typography fontWeight={500}>
                Social justice & inclusion
              </Typography>
            </Box>
          </MenuItem>
        </TextField>

      </DialogContent>

      <DialogActions>
        <DangerButton onClick={onClose}>
          Cancel
        </DangerButton>

        <PrimaryOutlinedButton onClick={onSave}>
          Save
        </PrimaryOutlinedButton>
      </DialogActions>
    </Dialog>
  );
};

export default StepOneDialog;